import type { ReactNode } from 'react';
import type { Editor } from '@tiptap/core';

interface ToolbarProps {
  editor: Editor | null;
}

export default function Toolbar({ editor }: ToolbarProps) {
  if (!editor) return null;

  const setLink = () => {
    const url = window.prompt('Enter URL');
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().unsetLink().run();
      return;
    }
    if (!/^(https?:|mailto:)/i.test(url.trim())) return;
    editor.chain().focus().setLink({ href: url }).run();
  };

  const btn = (label: string, active: boolean, onClick: () => void, children: ReactNode) => (
    <button
      type="button"
      aria-label={label}
      aria-pressed={active}
      onClick={onClick}
      className={`px-2 py-1 rounded text-sm font-medium transition-colors ${
        active
          ? 'bg-gray-200 dark:bg-gray-600 text-gray-900 dark:text-white'
          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
      }`}
    >
      {children}
    </button>
  );

  return (
    <div
      role="toolbar"
      aria-label="Formatting options"
      className="flex flex-wrap gap-1 p-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800"
    >
      {btn('Bold', editor.isActive('bold'), () => editor.chain().focus().toggleBold().run(), <strong>B</strong>)}
      {btn('Italic', editor.isActive('italic'), () => editor.chain().focus().toggleItalic().run(), <em>I</em>)}
      {btn('Heading 1', editor.isActive('heading', { level: 1 }), () => editor.chain().focus().toggleHeading({ level: 1 }).run(), 'H1')}
      {btn('Heading 2', editor.isActive('heading', { level: 2 }), () => editor.chain().focus().toggleHeading({ level: 2 }).run(), 'H2')}
      {btn('Unordered List', editor.isActive('bulletList'), () => editor.chain().focus().toggleBulletList().run(), 'UL')}
      {btn('Ordered List', editor.isActive('orderedList'), () => editor.chain().focus().toggleOrderedList().run(), 'OL')}
      {btn('Link', editor.isActive('link'), setLink, 'Link')}
    </div>
  );
}
